import {
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListSubheader,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import {
  EvacuationOrder,
  EvacuationWarning,
  EvacuationAdvisory,
  EvacuationShelterInPlace,
  EvacuationOrderGreen,
  EvacuationAdvisoryRed,
} from 'features/mapping/icons';
import { EVAC_ZONE_LAYER_COLORS } from '../../../../constants';
import { LegendSectionTitle } from './LegendSectionTitle';
import { LegendListItemText } from './LegendListItemText';

const patternAvatarBoxStyle = {
  width: '100%',
  height: '100%',
};

type EvacZoneGroupHeadingProps = {
  title: string;
  subtitle?: string;
};

const EvacZoneGroupHeading = (
  props: EvacZoneGroupHeadingProps,
): JSX.Element => {
  const { title, subtitle } = props;

  return (
    <ListSubheader disableSticky sx={{ mb: 1 }}>
      <Typography variant="h3" color="textPrimary">
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="body2" color="textSecondary">
          {subtitle}
        </Typography>
      ) : null}
    </ListSubheader>
  );
};

const useStyles = makeStyles()(() => ({
  evacuationOrderIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor: EVAC_ZONE_LAYER_COLORS.order_warning_advisory.orders,
    background: `url("${EvacuationOrder.data}")`,
    backgroundSize: 'cover',
    border: `1px solid ${EVAC_ZONE_LAYER_COLORS.order_warning_advisory.orders}`,
  },
  evacuationWarningIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor: EVAC_ZONE_LAYER_COLORS.order_warning_advisory.warnings,
    background: `url("${EvacuationWarning.data}")`,
    backgroundSize: 'cover',
    border: `1px solid ${EVAC_ZONE_LAYER_COLORS.order_warning_advisory.warnings}`,
  },
  evacuationAdvisoryIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor: EVAC_ZONE_LAYER_COLORS.ready_set_go_with_levels.advisories,
    background: `url("${EvacuationAdvisory.data}")`,
    backgroundSize: 'cover',
    border: `1px solid ${EVAC_ZONE_LAYER_COLORS.ready_set_go_with_levels.advisories}`,
  },
  evacuationShelterInPlaceIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor:
      EVAC_ZONE_LAYER_COLORS.order_warning_advisory.shelter_in_place,
    background: `url("${EvacuationShelterInPlace.data}")`,
    backgroundSize: 'cover',
    border: `1px solid ${EVAC_ZONE_LAYER_COLORS.order_warning_advisory.shelter_in_place}`,
  },
  evacuationOrderGreenIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor: EVAC_ZONE_LAYER_COLORS.ready_set_go.orders,
    background: `url("${EvacuationOrderGreen.data}")`,
    backgroundSize: 'cover',
    border: `1px solid ${EVAC_ZONE_LAYER_COLORS.ready_set_go_reverse_colors.orders}`,
  },
  evacuationAdvisoryRedIcon: {
    ...patternAvatarBoxStyle,
    backgroundColor:
      EVAC_ZONE_LAYER_COLORS.ready_set_go_reverse_colors.advisories,
    background: `url("${EvacuationAdvisoryRed.data}")`,
    backgroundSize: 'cover',
    border: `1px solid ${EVAC_ZONE_LAYER_COLORS.ready_set_go_reverse_colors.advisories}`,
  },
}));

export const EvacZonesLegend = (): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <List
      disablePadding
      dense
      subheader={
        <LegendSectionTitle titleSx={{ pb: 1 }}>
          {t('map.legends.evacuationZones.title')}
        </LegendSectionTitle>
      }
    >
      <List
        disablePadding
        dense
        subheader={
          <EvacZoneGroupHeading
            title={t('map.legends.evacuationZones.california')}
          />
        }
      >
        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.evacuationOrderIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.evacuationZones.legends.order.title')}
            secondary={t('map.legends.evacuationZones.legends.order.subtitle')}
          />
        </ListItem>

        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.evacuationWarningIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.evacuationZones.legends.warning.title')}
            secondary={t(
              'map.legends.evacuationZones.legends.warning.subtitle',
            )}
          />
        </ListItem>

        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.evacuationShelterInPlaceIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t(
              'map.legends.evacuationZones.legends.shelterInPlace.title',
            )}
            secondary={t(
              'map.legends.evacuationZones.legends.shelterInPlace.subtitle',
            )}
          />
        </ListItem>
      </List>

      <List
        disablePadding
        dense
        subheader={
          <EvacZoneGroupHeading
            title={t('map.legends.evacuationZones.readySetGo')}
          />
        }
      >
        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.evacuationOrderIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.evacuationZones.legends.order.level')}
            secondary={t('map.legends.evacuationZones.legends.order.subtitle')}
          />
        </ListItem>

        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.evacuationWarningIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.evacuationZones.legends.warning.level')}
            secondary={t(
              'map.legends.evacuationZones.legends.warning.subtitle',
            )}
          />
        </ListItem>

        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.evacuationAdvisoryIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.evacuationZones.legends.advisory.level')}
            secondary={t(
              'map.legends.evacuationZones.legends.advisory.subtitle',
            )}
          />
        </ListItem>
      </List>

      <List
        disablePadding
        dense
        subheader={
          <EvacZoneGroupHeading
            title={t('map.legends.evacuationZones.readySetGo')}
            subtitle={t('map.legends.evacuationZones.readySetGoReverseStates')}
          />
        }
      >
        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.evacuationOrderGreenIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.evacuationZones.legends.order.level')}
            secondary={t('map.legends.evacuationZones.legends.order.subtitle')}
          />
        </ListItem>

        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.evacuationWarningIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.evacuationZones.legends.warning.level')}
            secondary={t(
              'map.legends.evacuationZones.legends.warning.subtitle',
            )}
          />
        </ListItem>

        <ListItem alignItems="flex-start">
          <ListItemAvatar>
            <Avatar variant="square">
              <Box className={classes.evacuationAdvisoryRedIcon} />
            </Avatar>
          </ListItemAvatar>
          <LegendListItemText
            variant="watchduty-legend"
            primary={t('map.legends.evacuationZones.legends.advisory.level')}
            secondary={t(
              'map.legends.evacuationZones.legends.advisory.subtitle',
            )}
          />
        </ListItem>
      </List>
    </List>
  );
};
